import { Box, IconButton, InputBase, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import {
  liveTranscriptHeaderIconButtonSx,
  liveTranscriptHeaderToggleIconSx,
  liveTranscriptSearchBarSx,
  liveTranscriptSearchCountSx,
  liveTranscriptSearchIconSx,
  liveTranscriptSearchInputSx,
  liveTranscriptSearchInputWrapSx,
} from 'src/components/Assistant/live-transcript/live-transcript.styles';
import { LiveTranscriptSearchHeaderProps } from 'src/components/Assistant/live-transcript/live-transcript.types';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';

export const LiveTranscriptSearchHeader = ({
  searchQuery,
  totalMatches,
  currentMatchIndex,
  onSearchQueryChange,
  onPrevMatch,
  onNextMatch,
  onCloseSearch,
}: LiveTranscriptSearchHeaderProps) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const hasResults = totalMatches > 0;
  const resultsLabel = hasResults ? `${currentMatchIndex + 1} of ${totalMatches}` : 'No results';
  const countLabel = searchQuery ? resultsLabel : '';
  const navDisabled = totalMatches < 2;

  return (
    <Stack direction="row" sx={liveTranscriptSearchBarSx}>
      <Box sx={liveTranscriptSearchInputWrapSx}>
        <DynamicIcon name="search" sx={liveTranscriptSearchIconSx} />
        <InputBase
          inputRef={searchInputRef}
          placeholder="Search transcript"
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onCloseSearch();
              return;
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              if (totalMatches === 0) {
                return;
              }
              if (event.shiftKey) {
                onPrevMatch();
              } else {
                onNextMatch();
              }
            }
          }}
          sx={liveTranscriptSearchInputSx}
        />
        {countLabel ? (
          <Typography component="span" sx={liveTranscriptSearchCountSx(hasResults)}>
            {countLabel}
          </Typography>
        ) : null}
      </Box>
      <IconButton
        aria-label="Previous match"
        size="small"
        disabled={navDisabled}
        onClick={onPrevMatch}
        sx={liveTranscriptHeaderIconButtonSx}
      >
        <DynamicIcon name="expand_less" sx={liveTranscriptHeaderToggleIconSx} />
      </IconButton>
      <IconButton
        aria-label="Next match"
        size="small"
        disabled={navDisabled}
        onClick={onNextMatch}
        sx={liveTranscriptHeaderIconButtonSx}
      >
        <DynamicIcon name="expand_more" sx={liveTranscriptHeaderToggleIconSx} />
      </IconButton>
      <IconButton
        aria-label="Close search"
        size="small"
        onClick={onCloseSearch}
        sx={liveTranscriptHeaderIconButtonSx}
      >
        <DynamicIcon name="close" sx={liveTranscriptHeaderToggleIconSx} />
      </IconButton>
    </Stack>
  );
};
